import React, { useContext, useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import Container from 'components/Container';
import Navigation from 'components/Navigation';
import { HeaderContext } from 'contexts/HeaderContext';

import { handleEnterKey } from 'utils/helpers/accessible-events';
import { navigationLinks } from 'utils/helpers/navigation';

import Logo from './components/Logo';
import UserMenu from './components/UserMenu';

import styles from './styles.module.scss';

const Header = (props) => {
	const headerRef = useRef(null);
	const elementToFocusRef = useRef(null);
	const [headerTabIndex, setHeaderTabIndex] = useState(-1);
	const [headerContext] = useContext(HeaderContext);

	const changeTabIndex = (value) => {
		setHeaderTabIndex(value);
	};

	const handleFocus = (event) => {
		if (event.target === event.currentTarget) {
			setHeaderTabIndex(-1);
		}
	};

	const handleBlur = (event) => {
		const { relatedTarget, currentTarget } = event;
		const isHTML = relatedTarget?.nodeName === 'HTML';
		const isFocusLost = !currentTarget.contains(relatedTarget) || isHTML;

		if (isFocusLost) {
			setHeaderTabIndex(-1);
		}
	};

	const handleKeyDown = (event) => {
		handleEnterKey(event, () => {
			setHeaderTabIndex(0);
			event.preventDefault();

			elementToFocusRef.current.focus({ focusVisible: true });
		});
	};

	useEffect(() => {
		if (headerRef?.current) {
			headerRef.current?.focus({ focusVisible: false });
		}
	}, []);

	return (
		<header
			id="header"
			aria-label="Меню"
			aria-hidden="true"
			role="navigation"
			tabIndex={0}
			className={cx(
				styles.Header,
				styles[`Header_theme_${headerContext.theme}`]
			)}
			onKeyDown={handleKeyDown}
			onFocus={handleFocus}
			onBlur={handleBlur}
			ref={headerRef}
		>
			<Container innerClassName={styles.inner}>
				<div className={styles.brand}>
					<Logo tabIndex={headerTabIndex} logoRef={elementToFocusRef} />
				</div>
				<div className={styles.controls}>
					<div className={styles.navigation}>
						<Navigation
							primacy="primary"
							theme="main"
							data={navigationLinks.primary}
							rubricPath={props.rubricPath}
							parentTabIndex={headerTabIndex}
							changeParentTabIndex={changeTabIndex}
						/>
					</div>
					<div>
						<UserMenu
							tabIndex={headerTabIndex}
							changeTabIndex={changeTabIndex}
							headerRef={headerRef}
							{...props}
						/>
					</div>
				</div>
			</Container>
		</header>
	);
};

Header.propTypes = {
	rubricPath: PropTypes.string
};

Header.defaultProps = {
	rubricPath: null
};

export default Header;
